import React from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ArrowDown, ArrowUp } from 'lucide-react'

interface SpeedGaugeProps {
  downloadSpeed: number
  uploadSpeed: number
  isRunning: boolean
  currentTest: string | null
  progress: number
}

const SpeedGauge: React.FC<SpeedGaugeProps> = ({
  downloadSpeed,
  uploadSpeed,
  isRunning,
  currentTest,
  progress
}) => {
  const { t } = useTranslation()

  // SVG circle parameters
  const size = 280
  const strokeWidth = 12
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  
  // Calculate progress for the ring
  const maxSpeed = 200 // Mbps
  const currentSpeed = currentTest === 'upload' ? uploadSpeed : downloadSpeed
  const speedPercentage = Math.min((currentSpeed / maxSpeed) * 100, 100)
  const progressOffset = circumference - (speedPercentage / 100) * circumference

  const getCurrentSpeed = () => {
    if (isRunning) {
      if (currentTest === 'download') return downloadSpeed
      if (currentTest === 'upload') return uploadSpeed
      return 0
    }
    return Math.max(downloadSpeed, uploadSpeed)
  }

  const getGradientId = () => {
    if (currentTest === 'upload') return 'uploadGradient'
    return 'downloadGradient'
  }

  return (
    <div className="flex flex-col items-center space-y-8">
      {/* Main Gauge */}
      <div className="relative">
        <svg width={size} height={size} className="transform -rotate-90">
          <defs>
            <linearGradient id="downloadGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#007AFF" />
              <stop offset="100%" stopColor="#5AC8FA" />
            </linearGradient>
            <linearGradient id="uploadGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#34C759" />
              <stop offset="100%" stopColor="#30D158" />
            </linearGradient>
          </defs>
          
          {/* Background ring */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#F5F5F7"
            strokeWidth={strokeWidth}
          />
          
          {/* Progress ring */}
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={`url(#${getGradientId()})`}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset: progressOffset }}
            transition={{ 
              duration: 0.8, 
              ease: [0.25, 0.1, 0.25, 1]
            }}
          />
        </svg>
        
        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-6xl font-semibold text-apple-gray-900 num-display tabular-nums">
              {getCurrentSpeed() > 0 ? getCurrentSpeed().toFixed(1) : '--'}
            </div>
            <div className="text-lg text-apple-gray-500 mt-1">{t('mbps')}</div>
            
            {isRunning && (
              <motion.div 
                className="mt-4 flex items-center justify-center space-x-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="w-2 h-2 bg-apple-blue rounded-full animate-pulse" />
                <span className="text-sm text-apple-gray-500">
                  {currentTest === 'download' && t('testingDownload')}
                  {currentTest === 'upload' && t('testingUpload')}
                  {currentTest === 'ping' && t('testingPing')}
                  {currentTest === 'preparing' && t('preparing')}
                  {currentTest === 'analyzing' && t('analyzing')}
                </span>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Progress bar when running */}
      {isRunning && (
        <motion.div 
          className="w-64"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="h-1.5 bg-apple-gray-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-apple-blue rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <p className="text-center text-sm text-apple-gray-500 mt-2">
            {Math.round(progress)}%
          </p>
        </motion.div>
      )}

      {/* Speed indicators - always show, with placeholder when no results */}
      {!isRunning && (
        <div className="flex space-x-12">
          {/* Download */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-apple-blue/10 rounded-full">
              <ArrowDown className="w-5 h-5 text-apple-blue" />
            </div>
            <div>
              <div className="text-2xl font-semibold text-apple-gray-900 num-display tabular-nums">
                {downloadSpeed > 0 ? downloadSpeed.toFixed(1) : '--'}
              </div>
              <div className="text-xs text-apple-gray-500">{t('downloadSpeed')}</div>
            </div>
          </div>

          {/* Upload */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-apple-green/10 rounded-full">
              <ArrowUp className="w-5 h-5 text-apple-green" />
            </div>
            <div>
              <div className="text-2xl font-semibold text-apple-gray-900 num-display tabular-nums">
                {uploadSpeed > 0 ? uploadSpeed.toFixed(1) : '--'}
              </div>
              <div className="text-xs text-apple-gray-500">{t('uploadSpeed')}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SpeedGauge
